import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";


function autocompleteOptionBackground(theme: Theme, selected: boolean): string {
  if (!selected) {
    return "transparent";
  }
  return alpha(
    theme.palette.primary.main,
    theme.palette.action.selectedOpacity,
  );
}

function autocompleteOptionHoverBackground(theme: Theme, selected: boolean): string {
  if (!selected) {
    return theme.palette.action.hover;
  }
  return alpha(
    theme.palette.primary.main,
    theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
  );
}

export const serviceLineTreeFilterBorder = "#DADADA";
export const serviceLineTreeFilterPanelBg = "#EDECEC";
export const serviceLineTreeFilterPopoverBg = "#FFFFFF";
export const serviceLineTreeFilterTextPrimary = "#000000";
export const serviceLineTreeFilterFieldText = "#343A40";
export const serviceLineTreeFilterTextLeafNested = "#444242";

export const serviceLineTreeFilterLayout = {
  popoverWidthPx: 220,
  popoverRootPaddingPx: 4,
  treeBaseIndentPx: 17,
  treeDepthStepPx: 16,
  chevronColWidthPx: 16,
  chevronIconPx: 16,
  rowPaddingBottomPx: 6,
  fontSectionHeaderPx: 14,
  fontBranchPx: 13,
  fontLeafPx: 12,
  fontLineHeight: 1.26,
  fontWeightRegular: 400,
} as const;

const L = serviceLineTreeFilterLayout;

export const serviceLineTreeFilterStyles = {
  chevronIcon: {
    fontSize: L.chevronIconPx,
    color: serviceLineTreeFilterTextPrimary,
  } satisfies SxProps<Theme>,

  branchRowTypography: {
    fontSize: `${L.fontBranchPx}px`,
    lineHeight: L.fontLineHeight,
    color: serviceLineTreeFilterTextPrimary,
    fontWeight: L.fontWeightRegular,
  } satisfies SxProps<Theme>,

  sectionHeaderTypography: {
    fontSize: `${L.fontSectionHeaderPx}px`,
    lineHeight: L.fontLineHeight,
    fontWeight: L.fontWeightRegular,
    color: serviceLineTreeFilterTextPrimary,
  } satisfies SxProps<Theme>,

  allRowTypography: {
    fontSize: `${L.fontSectionHeaderPx}px`,
    lineHeight: L.fontLineHeight,
    fontWeight: L.fontWeightRegular,
    color: serviceLineTreeFilterTextPrimary,
  } satisfies SxProps<Theme>,

  fieldLabel: {
    fontSize: "12px",
    fontWeight: 400,
    color: serviceLineTreeFilterFieldText,
    mb: "0px",
  } satisfies SxProps<Theme>,

  fieldWrapper: {
    position: "relative",
    pt: "4px",
    minWidth: 150,
  } satisfies SxProps<Theme>,

  fieldClearRevealOnHover: {
    "& .service-line-clear-indicator": {
      visibility: "hidden",
    },
    "@media (pointer: fine)": {
      "&:hover .service-line-clear-indicator": {
        visibility: "visible",
      },
    },
  } satisfies SxProps<Theme>,

  /** Matches `SingleSelectDropdown` / Business Unit filter field styling */
  textField: (disabled: boolean): SxProps<Theme> => ({
    "& .MuiOutlinedInput-root": {
      minHeight: 40,
      cursor: disabled ? "default" : "pointer",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(218, 218, 218, 1)",
    },
    "& .MuiInputBase-input": {
      fontSize: "0.875rem",
      pl: "8px",
      py: "8px",
      color: serviceLineTreeFilterFieldText,
      textOverflow: "ellipsis",
      overflow: "hidden",
      cursor: disabled ? "default" : "pointer",
      "&::placeholder": {
        color: serviceLineTreeFilterFieldText,
        opacity: 1,
      },
    },
    "& .MuiInputBase-root": {
      cursor: disabled ? "default" : "pointer",
    },
  }),

  closedFieldBox: (disabled: boolean): SxProps<Theme> => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
    minHeight: 40,
    boxSizing: "border-box",
    pl: "14px",
    pr: "9px",
    py: "8px",
    borderRadius: "4px",
    border: "1px solid rgba(218, 218, 218, 1)",
    backgroundColor: "#FFFFFF",
    cursor: disabled ? "default" : "pointer",
    color: serviceLineTreeFilterFieldText,
    fontSize: "0.875rem",
  }),

  closedFieldValue: {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
    lineHeight: 1.4,
    color: serviceLineTreeFilterFieldText,
    verticalAlign: "middle",
  } satisfies SxProps<Theme>,

  closedFieldValueSlot: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  } satisfies SxProps<Theme>,

  fieldExpandIcon: {
    color: serviceLineTreeFilterFieldText,
    fontSize: 22,
  } satisfies SxProps<Theme>,

  fieldEndAdornmentRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    mr: -0.5,
  } satisfies SxProps<Theme>,

  fieldClearIconButton: {
    p: "2px",
    color: serviceLineTreeFilterFieldText,
  } satisfies SxProps<Theme>,

  fieldClearIcon: {
    fontSize: 18,
    color: serviceLineTreeFilterFieldText,
  } satisfies SxProps<Theme>,

  loadingProgress: (hasClearButton: boolean): SxProps<Theme> => ({
    left: "45%",
    top: "30%",
    position: "absolute",
    transform: "translate(-50%, -50%)",
  }),

  popoverPaper: {
    width: L.popoverWidthPx,
    minWidth: L.popoverWidthPx,
    maxWidth: "100%",
    maxHeight: 360,
    mt: 0.5,
    borderRadius: "4px",
    overflow: "hidden",
    overflowY: "auto",
    boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.35)",
    bgcolor: serviceLineTreeFilterPopoverBg,
  } satisfies SxProps<Theme>,

  popoverContent: {
    display: "flex",
    flexDirection: "column",
    p: `${L.popoverRootPaddingPx}px`,
    boxSizing: "border-box",
  } satisfies SxProps<Theme>,

  popoverEmptySearch: {
    py: 1.5,
    px: 0.5,
    fontSize: "0.8125rem",
    color: serviceLineTreeFilterFieldText,
    textAlign: "center",
  } satisfies SxProps<Theme>,

  allRowBox: (theme: Theme, isSelected: boolean): SxProps<Theme> => ({
    alignSelf: "stretch",
    px: `${L.popoverRootPaddingPx}px`,
    pb: `${L.popoverRootPaddingPx}px`,
    py: 1,
    cursor: "pointer",
    borderBottom: `1px solid ${serviceLineTreeFilterBorder}`,
    borderRadius: 1,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    bgcolor: autocompleteOptionBackground(theme, isSelected),
    "@media (hover: hover)": {
      "&:hover": {
        bgcolor: autocompleteOptionHoverBackground(theme, isSelected),
      },
    },
  }),

  grayPanel: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "stretch",
    p: "2px 2px 2px",
  } satisfies SxProps<Theme>,

  branchSectionRow: (
    theme: Theme,
    expanded: boolean,
    isSelected: boolean,
  ): SxProps<Theme> => ({
    display: "flex",
    alignItems: "center",
    borderBottom: expanded ? "none" : `1px solid ${serviceLineTreeFilterBorder}`,
    py: `${L.rowPaddingBottomPx}px`,
    borderRadius: 1,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    bgcolor: autocompleteOptionBackground(theme, isSelected),
    "@media (hover: hover)": {
      "&:hover": {
        bgcolor: autocompleteOptionHoverBackground(theme, isSelected),
      },
    },
  }),

  iconButton: {
    p: 0,
    width: L.chevronColWidthPx,
    height: L.chevronColWidthPx,
    minWidth: L.chevronColWidthPx,
  } satisfies SxProps<Theme>,

  sectionHeaderTitle: {
    flex: 1,
    minWidth: 0,
    wordBreak: "break-word",
  } satisfies SxProps<Theme>,

  treeColumn: {
    display: "flex",
    flexDirection: "column",
  } satisfies SxProps<Theme>,

  treeNodeWrapper: (hasChildren: boolean, isOpen: boolean): SxProps<Theme> => ({
    display: "flex",
    flexDirection: "column",
  }),

  treeRow: (
    theme: Theme,
    rowInsetPx: number,
    showDividerUnderLabel: boolean,
    isSelected: boolean,
  ): SxProps<Theme> => ({
    ml: `${rowInsetPx}px`,
    mr: 0,
    pr: 0,
    display: "flex",
    alignItems: "center",
    py: `${L.rowPaddingBottomPx}px`,
    borderBottom: showDividerUnderLabel
      ? `1px solid ${serviceLineTreeFilterBorder}`
      : "none",
    borderRadius: 1,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    bgcolor: autocompleteOptionBackground(theme, isSelected),
    "@media (hover: hover)": {
      "&:hover": {
        bgcolor: autocompleteOptionHoverBackground(theme, isSelected),
      },
    },
  }),

  treeChevronCell: {
    width: L.chevronColWidthPx,
    minWidth: L.chevronColWidthPx,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } satisfies SxProps<Theme>,

  treeLeafTypography: {
    fontSize: `${L.fontLeafPx}px`,
    lineHeight: L.fontLineHeight,
    fontWeight: L.fontWeightRegular,
  } satisfies SxProps<Theme>,

  treeRowLabel: {
    flex: 1,
    minWidth: 0,
    cursor: "pointer",
    wordBreak: "break-word",
  } satisfies SxProps<Theme>,
};
